<template>
  <WizardBox title="Bulk Price Update Tool">
    <template v-slot:tools>
      
    </template>
    <template v-slot:content>
      <FormGroup v-bind:form="updateForm">
        <FormWizard ref="toolWizard" :submitting="submitting" :tabList="tabList" @finished="handleSubmit">
          <template v-slot:content>

            <FormWizardStep :active="true" index="1">
              <div class="row">
                <div class="col-lg-12">
                  <h4>Update List</h4>
                </div>
              </div>
              <div class="row mt-2">
                <div class="col-lg-12">
                  <PriceBulkModifierEditor v-model="updateList" @update="toolDetailsUpdated()" :editing="true" />
                </div>
              </div>
            </FormWizardStep>

            <FormWizardStep index="2">
              <div class="row">
                <div class="col-lg-12">
                  <ActionDropMenu class="float-right">
                    <DropdownMenuItem @click="refreshPreviewList" icon="fa-sync-alt">Refresh Preview</DropdownMenuItem>
                  </ActionDropMenu>
                  <h4>Results Preview</h4>
                </div>
              </div>
              <div class="row mt-2">
                <div class="col-lg-12">
                  <Spinner v-show="queryData.querying" />
                  <StaticDataTable class="static-table-list">
                    <template v-slot:header>
                      <tr>
                        <th>Stock</th>
                        <th>Name</th>
                        <th>Type</th>
                        <th>Info</th>
                        <th>Price</th>
                      </tr>
                    </template>
                    <template v-slot:body>
                      <tr v-for="(result, index) in queryData.results" :key="index">
                        <td class="font-weight-bold">
                          <LinkStock :id="result.stock.id" :name="result.stock.code" />
                        </td>
                        <td>{{result.stock.name}}</td>
                        <td>
                          {{displayResultType(result)}}
                          <span v-if="result.type=='customer'">
                            - <LinkContact :id="result.contact.id" :name="result.contact.name" />
                          </span>
                        </td>
                        <td>{{result.description}}</td>
                        <td>
                          <span v-if="parseNumber(result.prevPrice) != parseNumber(result.newPrice)">
                            <span v-html="displayPriceChange(result.prevPrice, result.newPrice, result.stock.uomType)" />
                          </span>
                          <span v-else>
                          -
                          </span>
                        </td>
                      </tr>
                    </template>
                    <template v-slot:empty v-if="queryData.results.length == 0">No preview results found</template>
                  </StaticDataTable>
                </div>
              </div>
            </FormWizardStep>

            <FormWizardStep index="3">
              <NotificationBar type="error" v-if="appendPriceRequest && !appendPriceRequestError && !isEditingMode">
                <NotificationBarTitle>Error: </NotificationBarTitle>An existing price update of the same type already exists. Edit <strong>#{{appendPriceRequest.sequenceNumber}}</strong> directly to modify bulk edits
              </NotificationBar>
              <NotificationBar type="error" v-if="appendPriceRequest && appendPriceRequestError">
                <NotificationBarTitle>{{appendPriceRequestError}}</NotificationBarTitle>
              </NotificationBar>
              
              <div v-show="!isModifierListEmpty">
                <h4>Information</h4>
                <FormRow label="Price Update Type" v-if="queryData.info.type">
                  <LabelPriceRequestTypeBadge :type="queryData.info.type" />
                </FormRow>
                <FormRow label="Price Request Group">
                  <LinkPriceRequest :id="appendPriceRequest.id" :name="appendPriceRequest.sequenceNumber ? '#'+appendPriceRequest.sequenceNumber : 'Unknown'" v-if="appendPriceRequest && !isEditingMode" />
                  <LinkPriceRequest :id="priceRequestData.id" :name="priceRequestData.sequenceNumber ? '#'+priceRequestData.sequenceNumber : 'Unknown'" v-if="isEditingMode" />
                  <FormStaticLabel v-if="!appendPriceRequest && !isEditingMode">New Price Request</FormStaticLabel>
                </FormRow>

                <FormRow label="Schedule" v-if="!isEditingMode">
                  <FormOption label="Apply Now" name="scheduleType" setting="now" v-model="updateData.scheduleType" inline="true" field="scheduleType" />
                  <FormOption label="Scheduled" name="scheduleType" setting="schedule" v-model="updateData.scheduleType" inline="true" field="scheduleType" />
                </FormRow>

                <FormRow label="Scheduled Date" v-if="updateData.scheduleType == 'schedule' && !isEditingMode">
                  <FormDate field="scheduleStartDate" v-model="updateData.scheduleStartDate" />
                </FormRow>

                <FormRow label="Update Description" v-if="!isEditingMode">
                  <FormInputTextArea placeholder="Update Reason" v-model="updateData.reason" field="reason" />
                </FormRow>

                <FormHorizontalRule />
                <StaticDataTable class="static-table-list">
                  <template v-slot:header>
                    <tr>
                      <th>Warnings</th>
                    </tr>
                  </template>
                  <template v-slot:body>
                    <tr v-for="(warning, index) in queryData.warnings" :key="index">
                      <td><i class="text-warning fas fa-exclamation-triangle"></i> {{warning.description}}</td>
                    </tr>
                  </template>
                  <template v-slot:empty v-if="queryData.warnings.length == 0">No warnings found</template>
                </StaticDataTable>
              </div>

              <p class="text-muted text-center font-italic" v-if="isModifierListEmpty">Update list is empty. Add update items to continue</p>

            </FormWizardStep>
          </template>
        </FormWizard>
      </FormGroup>
    </template>
  </WizardBox>
</template>

<script>
import _ from 'lodash'
import Vue from 'vue'

import form from '@/mixins/form'
import WizardBox from '@/components/wizardBox'
import StaticDataTable from '@/components/table/staticDataTable.vue'
import PriceRequestMixin from '@/modules/pricing/mixins/priceRequestMixin'
import PriceUpdateItemDialog from '@/modules/pricing/views/priceUpdateItemDialog'
import LabelPriceRequestTypeBadge from '@/modules/pricing/components/labelPriceRequestTypeBadge'
import PriceBulkModifierEditor from '@/modules/pricing/components/priceBulkModifierEditor'

import { ApprovalTypes, PriceTypes, FilterOptionTypes, BulkUpdateAdjustmentTypes, InventoryStockTypes, InventoryStockTypeLookup, PriceRequestTypes, PricingRequestStatusTypes } from '@/store/types'

export default {
  name: 'PriceUpdateTool',
  mixins: [form, PriceRequestMixin],
  components: {
    WizardBox,
    StaticDataTable,
    LabelPriceRequestTypeBadge,
    PriceBulkModifierEditor
  },
  data() {
    return {
      updateForm: {},
      updateList: [ ],
      stockPriceRequestGroupId: null,
      priceRequestData: { },
      updateData: {
        scheduleType: 'now',
        scheduleStartDate: null,
        reason: ''
      },
      submitting: false,
      loading: false,
      loadingError: '',

      queryData: {
        querying: false,
        info: {},
        results: [],
        warnings: []
      },
      appendPriceRequest: null,
      appendPriceRequestError: '',

      results: {
        showing: false,
        list: []
      },

      tabList: [
        { title: 'Update Details' },
        { title: 'Preview' },
        { title: 'Confirm' }
      ],
    }
  },
  computed: {
    isEditingMode() {
      return this.stockPriceRequestGroupId != null
    },
    isModifierListEmpty() {
      return this.updateList.length == 0
    }
  },
  mounted() {
    this.stockPriceRequestGroupId = this.$route.params.id
    this.requestData()
  },
  created: function() {
    // Debounce to limit network usage
    this.debouncedQuery = _.debounce(this.requestQueryData, 500)
  },
  methods: {
    resetData() {
      this.updateData = {
        scheduleType: 'now',
        scheduleStartDate: null,
        reason: ''
      }
      this.submitting = false,

      this.queryData.querying = false
      this.queryData.results = []
      this.queryData.warnings = []

      this.results.showing = false
      this.results.list = []
    },
    requestData() {
      // If editing an existing price request, load the details
      if(this.isEditingMode) {
        this.loading = true
        this.$apiService.pricingService.getPriceRequest(this.stockPriceRequestGroupId)
        .then(response => {
          this.$data.priceRequestData = response.data
          this.updateData.reason = this.priceRequestData.reason || ''

          this.updateList = response.data.priceRequestModifierItems || []
          this.refreshPreviewList()
        })
        .catch(err => {
          this.loadingError = 'Failed to load price request data'
        })
        .finally(() => {
          this.$data.loading = false
        })
      }
    },
    handleSubmit() {
      this.submitting = true
      this.updateForm = this.clearFormResponse()

      var toolData = this.buildUpdateData()

      if(this.isModifierListEmpty) {
        this.warningMessage('Empty update list', 'At least one update item is required to create the update')
        return
      }
      // We no longer support appending updates to an existing price update
      // Have to just edit the bulk items directly
      if(this.appendPriceRequest) {
        this.warningMessage('Price update already exists', 'A price update already exists for this update type. Edit the bulk items via the price update page')
        return
      }

      if(this.isEditingMode) {
        this.$apiService.pricingService.updatePriceRequest(this.stockPriceRequestGroupId, toolData)
        .then(result => {
          // Go to the price request page
          this.successToast('Price Request Updated')
          this.$router.push(`/pricing/requests/${result.data.id}`)
        })
        .catch(err => {
          this.updateForm = this.processFormResponse(err)
          this.$refs.toolWizard.displayErrors()
        })
        .finally(() => {
          this.submitting = false
        })        

      } else {
        this.$apiService.pricingService.createPriceRequest(toolData)
        .then(result => {
          // Go to the price request page
          this.successToast('Price Request Created')
          this.$router.push(`/pricing/requests/${result.data.id}`)
        })
        .catch(err => {
          this.updateForm = this.processFormResponse(err)
          this.$refs.toolWizard.displayErrors()
        })
        .finally(() => {
          this.submitting = false
        })
      }
    },

    requestQueryData() {
      var toolData = this.buildUpdateData()

      this.queryData.querying = true
      this.queryData.results = []
      this.queryData.warnings = []

      this.appendPriceRequest = null
      this.appendPriceRequestError = ''

      this.$refs.toolWizard.setResultCount(1, 0)
      this.$refs.toolWizard.setWarningCount(2, 0)

      if(this.stockPriceRequestGroupId)
        toolData.stockPriceRequestGroupId = this.stockPriceRequestGroupId

      Promise.all([
        this.$apiService.pricingService.queryBulkPriceUpdate(toolData),
        this.$apiService.pricingService.getPriceRequestList({ type: PriceRequestTypes.sales, status: [PricingRequestStatusTypes.draft, PricingRequestStatusTypes.pendingApproval, PricingRequestStatusTypes.pendingSchedule, PricingRequestStatusTypes.processing, PricingRequestStatusTypes.generatingPriceLists] })
      ])
      .then(result => {

        this.queryData.info = result[0].data.info
        this.queryData.results = result[0].data.results
        this.queryData.warnings = result[0].data.warnings
        let salesPriceRequestList = result[1].data

        // If a sales price request, find the existing sales price request in the list we'll append to
        if(this.queryData.info.type == PriceRequestTypes.sales && salesPriceRequestList.length > 0 && !this.isEditingMode) {
          this.$data.appendPriceRequest = salesPriceRequestList[0]

          // If not in draft mode, show an error
          if(this.$data.appendPriceRequest.status != PricingRequestStatusTypes.draft) {
            this.appendPriceRequestError = 'Current price request group is not in draft mode. Move it to draft before continuing'
          }
        }

        this.$refs.toolWizard.setResultCount(1, this.queryData.results.length)
        this.$refs.toolWizard.setWarningCount(2, this.queryData.warnings.length)
      })
      .catch(err => {
        // Add this to warning list
        var formInfo = this.processFormResponse(err)
        this.queryData.warnings = [{
          description: formInfo.errorMessage
        }]

        this.$refs.toolWizard.setWarningCount(2, this.queryData.warnings.length)
      })
      .finally(() => {
        this.queryData.querying = false
      })
    },

    refreshPreviewList() {
      this.debouncedQuery()
    },

    toolDetailsUpdated() {
      // Details / Filter info updated. Run a query for results/warnings again
      this.debouncedQuery()
    },

    buildUpdateData() {
      var toolData = { }
      
      if(!this.stockPriceRequestGroupId) {
        toolData.isScheduled = this.updateData.scheduleType != 'now'
        toolData.scheduleStartDate = this.updateData.scheduleStartDate
        toolData.reason = this.updateData.reason
      }

      toolData.priceRequestModifierItems = this.updateList.map(updateItem => {
        var output = _.cloneDeep(updateItem)

        this.setFilterData(output, updateItem, 'customerFilter')
        this.setFilterData(output, updateItem, 'stockFilter')
        this.setFilterData(output, updateItem, 'stockTypeFilter')
        this.setFilterData(output, updateItem, 'categoryFilter')
        this.setFilterData(output, updateItem, 'priceGroupFilter')
        this.setFilterData(output, updateItem, 'serviceRangeFilter')

        return output
      })

      // Set the price request object to append this update too
      // Note: Not currently supported
      if(this.appendPriceRequest)
        toolData.stockPriceRequestGroupId = this.appendPriceRequest.id

      return toolData
    },

    setFilterData(outputData, itemData, filterName) {
      outputData[`${filterName}Type`] = itemData[filterName].type
      outputData[`${filterName}Value`] = itemData[filterName].valueList ? itemData[filterName].valueList : []
      outputData[`${filterName}Description`] = itemData[filterName].descriptionList ? itemData[filterName].descriptionList : []

      if(outputData[`${filterName}Type`] == null)
        outputData[`${filterName}Type`] = FilterOptionTypes.all
    },

    parseNumber(value) {
      if(value == null || isNaN(value))
        return 0
      return parseFloat(value)
    },
    displayResultType(result) {
      if(result.type == PriceTypes.sales)
        return 'Sales'
      if(result.type == PriceTypes.customer) {
        return 'Customer'
      }
    },

    isItemSales(item) {
      return item.priceType == PriceTypes.sales
    },
    isItemCustomer(item) {
      return item.priceType == PriceTypes.customer
    },
    displayItemFilterInfo(filterInfo) {
      var filterCount = 0
      if(filterInfo.valueList)
        filterCount = filterInfo.valueList.length

      if(filterInfo.type == FilterOptionTypes.all)
        return 'All'
      else if(filterInfo.type == FilterOptionTypes.include)
        return `<strong>Include ${filterCount} item${filterCount == 1 ? '' : 's'}</strong>`
      else if(filterInfo.type == FilterOptionTypes.exclude)
        return `<strong>Exclude ${filterCount} item${filterCount == 1 ? '' : 's'}</strong>`

      return 'All'
    },
    displayItemAdjustmentInfo(item) {
      var isPercent = item.adjustmentType == BulkUpdateAdjustmentTypes.increasePercent || item.adjustmentType == BulkUpdateAdjustmentTypes.decreasePercent
      var isAmount = item.adjustmentType == BulkUpdateAdjustmentTypes.increaseAmount || item.adjustmentType == BulkUpdateAdjustmentTypes.decreaseAmount
      var isPositive = item.adjustmentType == BulkUpdateAdjustmentTypes.increasePercent || item.adjustmentType == BulkUpdateAdjustmentTypes.increaseAmount

      var value = isPercent ? parseFloat(item.adjustPercentValue) : parseFloat(item.adjustAmountValue)

      let className = value == 0 ? '' : (isPositive ? 'financial-profit' : 'financial-loss')

      if(value == 0)
        return ''

      if(isAmount)      
        return `<span class="font-weight-bold ${className}">${isPositive ? '+' : '-'}` + this.$options.filters.formatCurrency(value) + `</span>`
      return `<span class="font-weight-bold ${className}">${isPositive ? '+' : '-'}` + this.$options.filters.formatPercent(value) + `</span>`
    },

    editPriceUpdateItem(index) {
      this.displayModal(PriceUpdateItemDialog, { 
        isCreateMode: false,
        lookupDetails: this.getItemDialogLookup(),
        updateItem: this.$data.updateList[index]
      })
      .then((priceItem) => {
        if(priceItem) {
          Vue.set(this.updateList, index, priceItem)

          this.toolDetailsUpdated()
        }
      })
    },
    removePriceUpdateItem(index) {
      this.$data.updateList.splice(index, 1)
      this.toolDetailsUpdated()
    },

    getItemDialogLookup() {
      return {
        customerLookup: this.$data.customerLookup,
        stockLookup: this.$data.stockLookup,
        categoryLookup: this.$data.categoryLookup,
        priceGroupLookup: this.$data.priceGroupLookup,
        serviceRangeLookup: this.$data.serviceRangeLookup,
      }
    }

  }
}
</script>